/**
 * TrafficThresholdCard — Story 3.4 (Referenz: settings.html, Notifications-Tab)
 *
 * Schwellenwert-Slider fuer den Traffic-Einbruch-Alarm (Story 3.3,
 * User-Feld traffic_drop_threshold): vorzeichenbehafteter Prozentwert
 * -20 % .. -80 % (BE liefert min/max/default in matrix.traffic_drop_threshold).
 *
 * Lesend: GET /api/v2/notifications/matrix (traffic_drop_threshold)
 * Schreibend: PATCH /api/v2/account/notification_settings { traffic_drop_threshold_pct }
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAlertMatrix } from './useAlertMatrix'
import { toast } from '../../components/Toast/useToast'
import { apiErrorMessage } from '../../lib/errors/apiErrorMessage'

const FALLBACK_MIN = -80
const FALLBACK_MAX = -20
const FALLBACK_DEFAULT = -50

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function TrafficThresholdCard() {
  const { t } = useTranslation('notifications')
  const sliderId = useId()
  const { matrix, isLoading, saveThreshold, isSavingThreshold } = useAlertMatrix()

  const threshold = matrix?.traffic_drop_threshold
  const min = threshold?.min ?? FALLBACK_MIN
  const max = threshold?.max ?? FALLBACK_MAX
  const serverValue = threshold?.value ?? threshold?.default ?? FALLBACK_DEFAULT

  const [localValue, setLocalValue] = useState<number | null>(null)

  // Server-Wert uebernehmen, sobald er sich aendert (Save/Refetch) —
  // adjust-during-render statt setState im Effect.
  const [prevServerValue, setPrevServerValue] = useState(serverValue)
  if (serverValue !== prevServerValue) {
    setPrevServerValue(serverValue)
    setLocalValue(null)
  }

  const value = localValue ?? serverValue
  const dirty = value !== serverValue

  const handleSave = async () => {
    try {
      await saveThreshold(value)
      toast.success(t('threshold.saveSuccess'))
    } catch (e) {
      toast.error(apiErrorMessage(e, t('threshold.saveError')))
    }
  }

  if (isLoading) {
    return (
      <div className="card card-pad" data-testid="traffic-threshold-card">
        <div className="skeleton" style={{ height: 120 }} />
      </div>
    )
  }

  return (
    <div className="card card-pad" data-testid="traffic-threshold-card">
      <h3>{t('threshold.title')}</h3>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 14px' }}>
        {t('threshold.subtitle')}
      </p>
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <input
          id={sliderId}
          type="range"
          min={min}
          max={max}
          step={5}
          value={value}
          onChange={(e) => setLocalValue(clamp(Number(e.target.value), min, max))}
          style={{ flex: 1, accentColor: 'var(--primary)', minWidth: 0 }}
          aria-label={t('threshold.sliderLabel')}
          data-testid="traffic-threshold-slider"
        />
        <span
          className="mono tnum"
          style={{ fontSize: 13, minWidth: 52, textAlign: 'right' }}
          data-testid="traffic-threshold-value"
        >
          {value}&nbsp;%
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '12px 0 0' }}>
        {t('threshold.hint', { value: Math.abs(value) })}
      </p>
      <button
        type="button"
        className="btn btn-sm btn-primary"
        style={{ marginTop: 12 }}
        onClick={handleSave}
        disabled={!dirty || isSavingThreshold}
        data-testid="traffic-threshold-save"
      >
        {isSavingThreshold ? t('threshold.saving') : t('threshold.save')}
      </button>
    </div>
  )
}
